import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import GlassCard from "@/components/common/GlassCard";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { TripleHint, bandColor, live, hasUnpublishedChanges } from "./seoUtils";
import api from "@/lib/api";

const TABS = ["Overview", "Meta", "Content", "Keywords", "Technical", "Hreflang", "Schema", "Social", "Links", "Images", "History"];

export default function SEOPageWorkspace({ page, initialTab, onTabChange, onChange, onSave, onAnalyze, onPublish, onAi, analyzing, canPublish, aiOut, onRestore, onSaveImages, onLinkTask, onValidateSchema }) {
  const [tab, setTab] = useState(initialTab || "Overview");
  const [alts, setAlts] = useState([]);
  const [schemaResult, setSchemaResult] = useState(null);
  const [liveSeo, setLiveSeo] = useState(null);
  const [siteOrigin, setSiteOrigin] = useState("");
  useEffect(() => { if (initialTab) setTab(initialTab); }, [initialTab]);
  useEffect(() => { setAlts((page?.images?.items || []).map((img) => ({ src: img.src, alt: img.alt || "" }))); }, [page?.id, page?.images?.items]);
  useEffect(() => {
    if (!page?.path) return;
    let cancelled = false;
    api.get("/seo/settings").then((r) => { if (!cancelled) setSiteOrigin((r.data.production_origin || "").replace(/\/$/, "")); }).catch(() => {});
    const path = page.path || "/";
    fetch(`${(process.env.REACT_APP_BACKEND_URL || "https://cas-admin-backend.onrender.com").replace(/\/$/, "")}/api/public/seo?path=${encodeURIComponent(path)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (!cancelled) setLiveSeo(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [page?.path, page?.publish_state]);
  const selectTab = (t) => {
    setTab(t);
    onTabChange?.(t);
  };
  if (!page) return <GlassCard className="p-10 text-center text-white/40">Select a page to open the workspace.</GlassCard>;
  const seo = page.seo || {};
  const technical = page.technical || {};
  const social = page.social || {};
  const score = page.seo_score;
  const setSeo = (key, value) => onChange({ ...page, seo: { ...seo, [key]: { ...(seo[key] || {}), configured: value } } });
  const setTech = (key, value) => onChange({ ...page, technical: { ...technical, [key]: { ...(technical[key] || {}), configured: value } } });
  const title = seo.title?.configured || "";
  const desc = seo.description?.configured || "";
  const canonical = technical.canonical?.configured || page.url || "";
  const og = social.open_graph || {};
  const hreflang = technical.hreflang || technical.hreflang_detected || [];
  const unpublished = hasUnpublishedChanges(page);
  const livePageUrl = siteOrigin ? `${siteOrigin}${page.path === "/" ? "/" : page.path}` : page.url;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-white">{page.name}</h2>
          <p className="text-xs font-mono text-white/50">{page.url}</p>
          {unpublished && <p className="text-xs text-amber-300 mt-1">Draft changes not live — Publish to update the public SEO API.</p>}
          {livePageUrl && (
            <a href={livePageUrl} target="_blank" rel="noreferrer" className="text-xs text-cyan-300/80 hover:text-cyan-200 mt-1 inline-block">Open live page ↗</a>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="w-14 h-14 rounded-full border border-white/10 flex items-center justify-center text-sm font-semibold" style={{ color: bandColor(score) }}>{score ?? "—"}</div>
          <Button variant="ghost" onClick={onAnalyze} disabled={analyzing} className="text-white/80">{analyzing ? "Analyzing…" : "Analyze"}</Button>
          <Button onClick={onSave} className="bg-white/10 text-white">Save draft</Button>
          <Button onClick={onPublish} disabled={!canPublish} title={!canPublish ? "Publishing requires SEO Manager or Admin" : "Publish to public SEO API"} className="btn-gradient text-white border-0">Publish</Button>
        </div>
      </div>
      <p className="text-xs text-white/40">Status: {page.publish_state || "draft"} · Save draft is private. Publish makes title, description, robots, and schema live.</p>
      <div className="flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button key={t} onClick={() => selectTab(t)} className={`px-3 h-8 rounded-lg text-xs ${tab === t ? "bg-white/10 text-white" : "text-white/50 hover:text-white"}`}>{t}</button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="grid lg:grid-cols-2 gap-4">
          <GlassCard className="p-5 space-y-2 text-sm text-white/70">
            <div>Status: {page.publish_state} {page.needs_reanalysis ? "· needs re-analysis" : ""}</div>
            <div>Source: {page.source} / {page.page_type}</div>
            <div>Last crawl: {page.last_crawled || "Never"}</div>
            <div>Last analysis: {page.last_analyzed || "Never"}</div>
            <div>HTTP: {technical.status ?? "—"} · {technical.response_ms ? `${technical.response_ms} ms` : ""}</div>
            <Button variant="ghost" className="text-white/80" onClick={() => onAi("explain")}>Get suggestions</Button>
          </GlassCard>
          <GlassCard className="p-5">
            <h3 className="text-sm text-white mb-2">Issues</h3>
            {(page.issues || []).length === 0 ? <p className="text-sm text-white/40">No issues from the last analysis.</p> : (
              <ul className="space-y-2 text-sm text-white/70">
                {page.issues.map((issue, i) => <li key={i}><span className="text-amber-300 mr-2">{issue.severity}</span>{issue.message}</li>)}
              </ul>
            )}
          </GlassCard>
          <GlassCard className="p-5 lg:col-span-2">
            <h3 className="text-sm text-white mb-3">Draft vs live preview</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Draft (configured)</p>
                <div className="rounded-lg border border-white/10 bg-black/30 p-3 space-y-1">
                  <p className="text-[#8ab4f8] text-base leading-snug truncate">{title || page.name}</p>
                  <p className="text-emerald-400/80 text-xs truncate">{canonical}</p>
                  <p className="text-white/60 text-xs line-clamp-2">{desc || "No description configured."}</p>
                </div>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Published (live API)</p>
                {liveSeo ? (
                  <div className="rounded-lg border border-white/10 bg-black/30 p-3 space-y-1">
                    <p className="text-[#8ab4f8] text-base leading-snug truncate">{liveSeo.title || "—"}</p>
                    <p className="text-emerald-400/80 text-xs truncate">{liveSeo.canonical || "—"}</p>
                    <p className="text-white/60 text-xs line-clamp-2">{liveSeo.description || "—"}</p>
                    <p className="text-[10px] text-white/30 mt-1">state: {liveSeo.publish_state || "unknown"}</p>
                  </div>
                ) : (
                  <p className="text-sm text-white/40">Loading live SEO…</p>
                )}
              </div>
            </div>
          </GlassCard>
        </div>
      )}

      {tab === "Meta" && (
        <div className="grid lg:grid-cols-2 gap-4">
          <GlassCard className="p-5 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/50">SEO title</Label>
              <Input value={title} onChange={(e) => setSeo("title", e.target.value)} className="bg-black/20 border-white/10 text-white mt-1" />
              <p className={`text-[11px] mt-1 ${title.length >= 40 && title.length <= 60 ? "text-emerald-300" : "text-amber-300"}`}>{title.length} / 40–60</p>
              <TripleHint field={seo.title} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/50">Meta description</Label>
              <Textarea value={desc} onChange={(e) => setSeo("description", e.target.value)} rows={3} className="bg-black/20 border-white/10 text-white mt-1" />
              <p className={`text-[11px] mt-1 ${desc.length >= 120 && desc.length <= 160 ? "text-emerald-300" : "text-amber-300"}`}>{desc.length} / 120–160</p>
              <TripleHint field={seo.description} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/50">Canonical</Label>
              <select className="mt-1 w-full h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2" value={technical.canonical_mode || "automatic"} onChange={(e) => onChange({ ...page, technical: { ...technical, canonical_mode: e.target.value } })}>
                <option value="automatic">Automatic</option>
                <option value="self">Self</option>
                <option value="custom">Custom</option>
              </select>
              <Input value={canonical} onChange={(e) => setTech("canonical", e.target.value)} className="bg-black/20 border-white/10 text-white mt-2" />
              <TripleHint field={technical.canonical} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-white/50">Indexing</Label>
              <div className="flex gap-3 mt-2 text-sm text-white/70">
                <label className="flex items-center gap-2"><input type="checkbox" checked={!(technical.robots?.configured || "").includes("noindex")} onChange={(e) => setTech("robots", `${e.target.checked ? "index" : "noindex"},${(technical.robots?.configured || "index,follow").includes("nofollow") ? "nofollow" : "follow"}`)} /> Index</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!(technical.robots?.configured || "").includes("nofollow")} onChange={(e) => setTech("robots", `${(technical.robots?.configured || "").includes("noindex") ? "noindex" : "index"},${e.target.checked ? "follow" : "nofollow"}`)} /> Follow</label>
              </div>
            </div>
          </GlassCard>
          <GlassCard className="p-5 space-y-4">
            <div className="rounded-xl bg-white p-4">
              <p className="text-[11px] text-[#70757a] uppercase">Google desktop</p>
              <p className="text-[12px] text-[#202124] truncate">{canonical || page.url}</p>
              <p className="text-[20px] text-[#1a0dab]">{title || live(seo.title) || "Untitled"}</p>
              <p className="text-[13px] text-[#4d5156]">{(desc || live(seo.description) || "").slice(0, 160)}</p>
            </div>
            <div className="rounded-xl bg-white p-4 max-w-[360px]">
              <p className="text-[11px] text-[#70757a] uppercase">Google mobile</p>
              <p className="text-[11px] text-[#202124] truncate">{canonical || page.url}</p>
              <p className="text-[16px] text-[#1a0dab] leading-tight">{title || live(seo.title) || "Untitled"}</p>
              <p className="text-[12px] text-[#4d5156]">{(desc || live(seo.description) || "").slice(0, 120)}</p>
            </div>
            <div className="rounded-xl bg-[#1b1f23] p-4 border border-white/10">
              <p className="text-[11px] text-white/40 uppercase">X / Twitter</p>
              <p className="text-white font-medium">{og.title || title}</p>
              <p className="text-sm text-white/60">{og.description || desc}</p>
            </div>
            <div className="rounded-xl bg-[#1877F2]/20 p-4 border border-[#1877F2]/30">
              <p className="text-[11px] text-white/50 uppercase">Facebook</p>
              <p className="text-white font-medium">{og.title || title}</p>
              <p className="text-sm text-white/60">{og.description || desc}</p>
            </div>
            <div className="rounded-xl bg-[#0A66C2]/20 p-4 border border-[#0A66C2]/30">
              <p className="text-[11px] text-white/50 uppercase">LinkedIn</p>
              <p className="text-white font-medium">{og.title || title}</p>
              <p className="text-sm text-white/60">{og.description || desc}</p>
            </div>
          </GlassCard>
        </div>
      )}

      {tab === "Content" && (
        <GlassCard className="p-5 text-sm text-white/70 space-y-2">
          <p>Words: {page.content?.word_count ?? "—"} · H1: {(page.content?.h1 || []).join(" | ") || "—"}</p>
          <p>H2: {(page.content?.h2 || []).join(" · ") || "None detected"}</p>
          <p className="text-white/50">{page.content?.text_excerpt || "Analyze the URL to extract copy."}</p>
          <Button variant="ghost" className="text-white/80" onClick={() => onAi("improve_content")}>Get content suggestions</Button>
        </GlassCard>
      )}

      {tab === "Keywords" && (
        <GlassCard className="p-5 space-y-3">
          {(page.keywords || []).map((k, i) => (
            <div key={i} className="grid grid-cols-[1fr_120px_auto] gap-2">
              <Input value={k.keyword} onChange={(e) => {
                const next = [...(page.keywords || [])]; next[i] = { ...k, keyword: e.target.value }; onChange({ ...page, keywords: next });
              }} className="bg-black/20 border-white/10 text-white" />
              <select value={k.type} onChange={(e) => { const next = [...(page.keywords || [])]; next[i] = { ...k, type: e.target.value }; onChange({ ...page, keywords: next }); }} className="h-10 rounded-md bg-black/20 border border-white/10 text-white text-sm px-2">
                <option value="primary">primary</option><option value="secondary">secondary</option><option value="supporting">supporting</option>
              </select>
              <button className="text-white/40" onClick={() => onChange({ ...page, keywords: page.keywords.filter((_, idx) => idx !== i) })} aria-label="Remove keyword"><Icons.X className="w-4 h-4" /></button>
            </div>
          ))}
          <Button variant="ghost" className="text-white/80" onClick={() => onChange({ ...page, keywords: [...(page.keywords || []), { keyword: "", type: "supporting" }] })}>Add keyword</Button>
          <p className="text-xs text-white/40">Search Console position/CTR stay empty until a connection exists.</p>
        </GlassCard>
      )}

      {tab === "Technical" && (
        <GlassCard className="p-5 text-sm text-white/70 space-y-2">
          <p>HTTPS: {(page.url || "").startsWith("https") ? "Yes" : "No"}</p>
          <p>Redirect hops: {(technical.hops || []).map((h) => h.status).join(" → ") || "None"}</p>
          <p>Crawl error: {technical.crawl_error || "None"}</p>
          <p>Robots: {technical.robots?.configured || technical.robots?.published || "index,follow"}</p>
        </GlassCard>
      )}

      {tab === "Hreflang" && (
        <GlassCard className="p-5 space-y-3">
          <p className="text-sm text-white/50">Detected: {(technical.hreflang_detected || []).map((h) => `${h.lang}:${h.href}`).join(" · ") || "none"}</p>
          {(Array.isArray(hreflang) ? hreflang : []).map((row, i) => (
            <div key={i} className="grid grid-cols-[120px_1fr_auto] gap-2">
              <Input value={row.lang || ""} onChange={(e) => {
                const next = [...hreflang]; next[i] = { ...row, lang: e.target.value }; onChange({ ...page, technical: { ...technical, hreflang: next } });
              }} className="bg-black/20 border-white/10 text-white" placeholder="en-IN" />
              <Input value={row.href || ""} onChange={(e) => {
                const next = [...hreflang]; next[i] = { ...row, href: e.target.value }; onChange({ ...page, technical: { ...technical, hreflang: next } });
              }} className="bg-black/20 border-white/10 text-white" placeholder="https://…" />
              <button className="text-white/40" onClick={() => onChange({ ...page, technical: { ...technical, hreflang: hreflang.filter((_, idx) => idx !== i) } })}>x</button>
            </div>
          ))}
          <Button variant="ghost" className="text-white/80" onClick={() => onChange({ ...page, technical: { ...technical, hreflang: [...(Array.isArray(hreflang) ? hreflang : []), { lang: "en", href: page.url }] } })}>Add hreflang</Button>
        </GlassCard>
      )}

      {tab === "Schema" && (
        <GlassCard className="p-5 space-y-2">
          <Textarea rows={12} value={typeof page.schema === "object" ? (page.schema.configured || "") : (page.schema || "")} onChange={(e) => onChange({ ...page, schema: { ...(typeof page.schema === "object" ? page.schema : {}), configured: e.target.value } })} className="bg-black/40 border-white/10 text-white font-mono text-xs" />
          <div className="flex gap-2 flex-wrap">
            {["Organization", "WebPage", "Service", "FAQPage", "Article"].map((t) => (
              <Button key={t} size="sm" variant="ghost" className="text-white/70" onClick={() => onAi("generate_schema")}>{t} template</Button>
            ))}
            {onValidateSchema && (
              <Button size="sm" variant="ghost" className="text-white/70" onClick={async () => {
                const raw = typeof page.schema === "object" ? (page.schema.configured || "") : (page.schema || "");
                setSchemaResult(await onValidateSchema(raw));
              }}>Validate JSON-LD</Button>
            )}
          </div>
          {schemaResult && <p className={`text-sm ${schemaResult.ok ? "text-emerald-300" : "text-amber-300"}`}>{schemaResult.ok ? "Valid JSON-LD" : (schemaResult.errors || []).join(" ")}</p>}
          <p className="text-xs text-white/40">Suggestions are rule-based (not a live LLM). Save draft, then Publish to ship schema.</p>
        </GlassCard>
      )}

      {tab === "Social" && (
        <GlassCard className="p-5 space-y-3">
          <Input value={og.title || ""} onChange={(e) => onChange({ ...page, social: { ...social, open_graph: { ...og, title: e.target.value } } })} placeholder="OG title" className="bg-black/20 border-white/10 text-white" />
          <Input value={og.description || ""} onChange={(e) => onChange({ ...page, social: { ...social, open_graph: { ...og, description: e.target.value } } })} placeholder="OG description" className="bg-black/20 border-white/10 text-white" />
          <Input value={og.image || ""} onChange={(e) => onChange({ ...page, social: { ...social, open_graph: { ...og, image: e.target.value } } })} placeholder="OG image URL" className="bg-black/20 border-white/10 text-white" />
        </GlassCard>
      )}

      {tab === "Links" && (
        <GlassCard className="p-5 text-sm text-white/70 space-y-2">
          <p>Internal: {(page.links?.internal || []).length} · Outbound: {(page.links?.outbound || []).length}</p>
          {(page.recommendations || []).map((r, i) => (
            <div key={i} className="flex flex-wrap gap-2 items-center border-b border-white/5 py-2">
              <span className="flex-1">{r.anchor} → {r.target_url} · {r.reason}</span>
              <button className="text-white/40" onClick={() => onChange({ ...page, recommendations: page.recommendations.filter((_, idx) => idx !== i) })}>Ignore</button>
              <button className="text-white/70" onClick={() => onLinkTask && onLinkTask(r)}>Task</button>
            </div>
          ))}
        </GlassCard>
      )}

      {tab === "Images" && (
        <GlassCard className="p-5 text-sm text-white/70 space-y-3">
          <p>Total {page.images?.total ?? 0} · Missing ALT {page.images?.missing_alt ?? 0} · Oversized {page.images?.oversized ?? 0}</p>
          {alts.map((img, i) => (
            <div key={img.src || i} className="grid md:grid-cols-[1fr_2fr] gap-2">
              <span className="font-mono text-[11px] truncate text-white/40">{img.src}</span>
              <Input value={img.alt} onChange={(e) => setAlts((rows) => rows.map((row, idx) => (idx === i ? { ...row, alt: e.target.value } : row)))} className="bg-black/20 border-white/10 text-white" placeholder="ALT text" />
            </div>
          ))}
          {onSaveImages && <Button onClick={() => onSaveImages(alts)} className="btn-gradient text-white border-0">Save ALT drafts</Button>}
        </GlassCard>
      )}

      {tab === "History" && (
        <GlassCard className="p-5 text-sm text-white/60 space-y-2">
          {(page.history || []).length === 0 ? "No revisions yet." : page.history.map((h) => (
            <div key={h.id} className="flex justify-between border-b border-white/5 py-2 gap-2">
              <span>{h.field}</span>
              <span>{h.user_email} · {h.timestamp?.slice(0, 16)}</span>
              {onRestore && <button className="text-cyan-300" onClick={() => onRestore(h.id)}>Restore</button>}
            </div>
          ))}
        </GlassCard>
      )}

      {aiOut && <GlassCard className="p-4 text-sm text-white/70"><pre className="whitespace-pre-wrap text-xs">{JSON.stringify(aiOut, null, 2)}</pre></GlassCard>}
    </div>
  );
}
